import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { Button, Input } from '../ui';
import { validateEmail } from '../../utils/validation';
import { track, identify, EVENTS } from '../../utils/analytics';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (email: string) => void;
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
}

export default function AuthModal({ isOpen, onClose, onSuccess, title, subtitle, ctaLabel }: AuthModalProps) {
  const navigate = useNavigate();
  const { state, setAuthEmail, setApiStatus } = useApp();
  const { auth, flow } = state;
  const isExpress = flow === 'express';

  const [errors, setErrors] = useState<{ email?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  function validate() {
    const newErrors: { email?: string } = {};

    const emailError = validateEmail(auth.email);
    if (emailError) newErrors.email = emailError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validate()) return;

    setSubmitting(true);

    identify(auth.email);
    track(EVENTS.AUTH_SUBMITTED, { flow: state.flow, email: auth.email });

    if (onSuccess) {
      onSuccess(auth.email);
      return;
    }

    // Set API status to loading and navigate to loading screen
    // The LoadingScreen will make the actual API call
    setApiStatus('loading');
    navigate('/loading');
  }

  function handleBackdropClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  return (
    <>
      <div className="auth-modal-backdrop" onClick={handleBackdropClick} />
      <div className="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
        <button className="auth-modal__close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="auth-modal__content">
          <div className="auth-modal__header">
            <h2 id="auth-modal-title" className="auth-modal__title">
              {title ?? (isExpress ? 'Your setup is ready' : 'Your plan is ready')}
            </h2>
            <p className="auth-modal__subtitle">
              {subtitle ?? (isExpress
                ? 'Enter your email to see your free financial setup'
                : 'Enter your email to see your free financial blueprint')}
            </p>
          </div>

          <form className="auth-modal__form" onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="email"
              placeholder="you@example.com"
              value={auth.email}
              onChange={setAuthEmail}
              error={errors.email}
              size="lg"
            />

            <div className="auth-modal__submit">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={submitting}
              >
                {ctaLabel ?? (isExpress ? 'Create My Financial Setup' : 'Get My Free Plan')}
              </Button>
            </div>

            <p className="auth-modal__consent-text">
              By clicking above, you consent to receive email updates from Sequence. You can unsubscribe at any time.
            </p>
          </form>

          <div className="auth-modal__trust">
            <span className="auth-modal__trust-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 12 15 16 10" />
              </svg>
            </span>
            <span>Your data is encrypted and secure. No spam, ever.</span>
          </div>
        </div>
      </div>
    </>
  );
}
